import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const InventoryManagement = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hostelId, setHostelId] = useState(null);

  const categories = ['vegetables', 'fruits', 'grains', 'dairy', 'spices', 'oil', 'other'];

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get hostel ID from userInfo or storage
  useEffect(() => {
    const id = userInfo?.hostelId || sessionStorage.getItem('hostelId') || 1;
    setHostelId(id);
  }, [userInfo]);

  // Fetch inventory data
  useEffect(() => {
    if (hostelId) {
      fetchInventoryData();
    }
  }, [hostelId]);

  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/mess/inventory?hostel_id=${hostelId}`);
      const data = await response.json();

      if (data.success) {
        setInventoryItems(data.items || []);
        filterItems(data.items || [], selectedCategory, searchTerm);
        fetchLowStockItems();
      } else {
        setError(data.message || 'Failed to load inventory');
      }
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchLowStockItems = async () => {
    try {
      const response = await fetch(`${API_URL}/mess/inventory/low-stock/${hostelId}`);
      const data = await response.json();
      if (data.success) {
        setLowStockItems(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching low stock items:', err);
    }
  };

  const filterItems = (items, category, search) => {
    let filtered = items;

    if (category !== 'all') {
      filtered = filtered.filter(item => item.category === category);
    }

    if (search) {
      filtered = filtered.filter(item =>
        item.item_name.toLowerCase().includes(search.toLowerCase()) ||
        item.supplier_name?.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredItems(filtered);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    filterItems(inventoryItems, category, searchTerm);
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    filterItems(inventoryItems, selectedCategory, term);
  };

  const handleAddItem = async (itemData) => {
    try {
      const payload = {
        ...itemData,
        hostel_id: hostelId
      };

      const response = await fetch(`${API_URL}/mess/inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        setShowAddModal(false);
        fetchInventoryData();
      } else {
        setError(data.message || 'Failed to add item');
      }
    } catch (err) {
      console.error('Error adding item:', err);
      setError('Failed to add item');
    }
  };

  const handleUpdateItem = async (itemId, itemData) => {
    try {
      const response = await fetch(`${API_URL}/mess/inventory/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData)
      });

      const data = await response.json();
      if (data.success) {
        setEditingItem(null);
        fetchInventoryData();
      } else {
        setError(data.message || 'Failed to update item');
      }
    } catch (err) {
      console.error('Error updating item:', err);
      setError('Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const response = await fetch(`${API_URL}/mess/inventory/${itemId}`, {
          method: 'DELETE'
        });

        const data = await response.json();
        if (data.success) {
          fetchInventoryData();
        } else {
          setError(data.message || 'Failed to delete item');
        }
      } catch (err) {
        console.error('Error deleting item:', err);
        setError('Failed to delete item');
      }
    }
  };

  const handleNavigation = (page) => {
    if (isMobile) {
      setSidebarOpen(false);
    }
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const MenuItem = ({ label, icon, onClick }) => (
    <div className="menu-item" onClick={onClick}>
      <span className="menu-icon">{icon}</span>
      <span className="menu-label">{label}</span>
    </div>
  );

  const InventoryStats = () => {
    const totalItems = inventoryItems.length;
    const totalValue = inventoryItems.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) * parseFloat(item.unit_price)), 0
    );
    const lowStockCount = lowStockItems.length;

    return (
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-emoji">📦</span>
            <span className="metric-label">Total Items</span>
          </div>
          <div className="metric-value">{totalItems}</div>
          <div className="metric-footer">
            <div className="metric-bar">
              <div className="metric-fill" style={{ width: '100%' }}></div>
            </div>
            <div className="metric-details">
              <span className="metric-total">{totalItems}</span>
              <span className="metric-trend">All Items</span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-emoji">⚠️</span>
            <span className="metric-label">Low Stock Items</span>
          </div>
          <div className="metric-value">{lowStockCount}</div>
          <div className="metric-footer">
            <div className="metric-bar">
              <div className="metric-fill" style={{ width: `${(lowStockCount / (totalItems || 1)) * 100}%`, backgroundColor: '#ef4444' }}></div>
            </div>
            <div className="metric-details">
              <span className="metric-total">{totalItems}</span>
              <span className="metric-trend">Need attention</span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-emoji">💰</span>
            <span className="metric-label">Total Value</span>
          </div>
          <div className="metric-value">₹{totalValue.toFixed(0)}</div>
          <div className="metric-footer">
            <div className="metric-bar">
              <div className="metric-fill" style={{ width: '100%' }}></div>
            </div>
            <div className="metric-details">
              <span className="metric-total">Inventory Worth</span>
              <span className="metric-trend">↑ Updated</span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-emoji">📊</span>
            <span className="metric-label">Categories</span>
          </div>
          <div className="metric-value">{new Set(inventoryItems.map(i => i.category)).size}</div>
          <div className="metric-footer">
            <div className="metric-bar">
              <div className="metric-fill" style={{ width: '100%' }}></div>
            </div>
            <div className="metric-details">
              <span className="metric-total">Active Categories</span>
              <span className="metric-trend">In Use</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AddEditModal = ({ item, onClose, onSave }) => {
    const [formData, setFormData] = useState(item || {
      item_name: '',
      category: 'other',
      quantity: 0,
      unit: 'kg',
      minimum_quantity: 5,
      unit_price: 0,
      supplier_name: '',
      last_purchased_date: '',
      expiry_date: '',
      is_perishable: 0
    });

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
      });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{item ? '✏️ Edit Item' : '➕ Add New Item'}</h2>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-row">
              <div className="form-group">
                <label>Item Name *</label>
                <input
                  type="text"
                  name="item_name"
                  value={formData.item_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Quantity *</label>
                <input
                  type="number"
                  name="quantity"
                  step="0.01"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Unit *</label>
                <input
                  type="text"
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  placeholder="kg, liters, pieces"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Minimum Quantity</label>
                <input
                  type="number"
                  name="minimum_quantity"
                  step="0.01"
                  value={formData.minimum_quantity}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Unit Price (₹)</label>
                <input
                  type="number"
                  name="unit_price"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Supplier Name</label>
                <input
                  type="text"
                  name="supplier_name"
                  value={formData.supplier_name || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Last Purchased Date</label>
                <input
                  type="date"
                  name="last_purchased_date"
                  value={formData.last_purchased_date || ''}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Expiry Date</label>
                <input
                  type="date"
                  name="expiry_date"
                  value={formData.expiry_date || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="is_perishable"
                    checked={formData.is_perishable === 1}
                    onChange={handleChange}
                  />
                  Perishable Item
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-save">Save Item</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const LowStockAlert = () => {
    if (lowStockItems.length === 0) return null;

    return (
      <div className="alert-section">
        <div className="alert-header">⚠️ Low Stock Items - Immediate Attention Needed</div>
        <div className="alert-items">
          {lowStockItems.slice(0, 5).map(item => (
            <div key={item.item_id} className="alert-item">
              <div className="alert-item-info">
                <span className="alert-item-name">{item.item_name}</span>
                <span className="alert-item-detail">Current: {parseFloat(item.quantity).toFixed(2)}{item.unit} | Min: {parseFloat(item.minimum_quantity).toFixed(2)}{item.unit}</span>
              </div>
              <span className="alert-badge">{item.quantity < item.minimum_quantity / 2 ? '🔴' : '🟡'}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const InventoryTable = () => {
    if (loading) {
      return <div className="loading">Loading inventory...</div>;
    }

    if (filteredItems.length === 0) {
      return <div className="empty-state">No items found. Try adjusting your filters or add new items.</div>;
    }

    return (
      <>
        {/* Desktop Table View */}
        <div className="table-container">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Category</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Supplier</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => {
                const isLowStock = item.quantity < item.minimum_quantity;
                const isExpired = item.expiry_date && new Date(item.expiry_date) < new Date();

                return (
                  <tr key={item.item_id} className={isLowStock ? 'row-warning' : ''}>
                    <td className="item-name">
                      {item.item_name}
                      {item.is_perishable ? ' 🍎' : ''}
                    </td>
                    <td>
                      <span className="category-badge" data-category={item.category}>
                        {item.category}
                      </span>
                    </td>
                    <td className="quantity">
                      <span className={isLowStock ? 'low-stock' : ''}>
                        {parseFloat(item.quantity).toFixed(2)} {item.unit}
                      </span>
                    </td>
                    <td>₹{parseFloat(item.unit_price).toFixed(2)}</td>
                    <td>{item.supplier_name || '-'}</td>
                    <td>
                      <div className="status-cell">
                        {isExpired && <span className="status-badge expired">Expired</span>}
                        {isLowStock && <span className="status-badge warning">Low Stock</span>}
                        {!isExpired && !isLowStock && <span className="status-badge good">OK</span>}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon edit"
                          onClick={() => setEditingItem(item)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon delete"
                          onClick={() => handleDeleteItem(item.item_id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="table-container">
          {filteredItems.map(item => {
            const isLowStock = parseFloat(item.quantity) < parseFloat(item.minimum_quantity);
            const isExpired = item.expiry_date && new Date(item.expiry_date) < new Date();

            return (
              <div key={item.item_id} className={`inventory-card ${isLowStock ? 'warning' : ''}`}>
                <div className="card-header">
                  <div className={`card-title ${item.is_perishable ? 'perishable' : ''}`}>
                    {item.item_name}
                  </div>
                  <div className="card-status">
                    {isExpired && <span className="status-badge expired">Expired</span>}
                    {isLowStock && <span className="status-badge warning">Low Stock</span>}
                    {!isExpired && !isLowStock && <span className="status-badge good">OK</span>}
                  </div>
                </div>

                <div className="card-row">
                  <span className="card-label">Category</span>
                  <span className="card-category" data-category={item.category}>
                    {item.category}
                  </span>
                </div>

                <div className="card-row">
                  <span className="card-label">Quantity</span>
                  <span className={`card-value ${isLowStock ? 'low-stock' : 'quantity'}`}>
                    {parseFloat(item.quantity).toFixed(2)} {item.unit}
                  </span>
                </div>

                <div className="card-row">
                  <span className="card-label">Min. Quantity</span>
                  <span className="card-value">
                    {parseFloat(item.minimum_quantity).toFixed(2)} {item.unit}
                  </span>
                </div>

                <div className="card-row">
                  <span className="card-label">Unit Price</span>
                  <span className="card-value">₹{parseFloat(item.unit_price).toFixed(2)}</span>
                </div>

                <div className="card-row">
                  <span className="card-label">Supplier</span>
                  <span className="card-value">{item.supplier_name || '-'}</span>
                </div>

                {item.last_purchased_date && (
                  <div className="card-row">
                    <span className="card-label">Last Purchased</span>
                    <span className="card-value">{item.last_purchased_date}</span>
                  </div>
                )}

                {item.expiry_date && (
                  <div className="card-row">
                    <span className="card-label">Expiry Date</span>
                    <span className="card-value">{item.expiry_date}</span>
                  </div>
                )}

                <div className="card-footer">
                  <div></div>
                  <div className="card-actions">
                    <button
                      className="btn-icon edit"
                      onClick={() => setEditingItem(item)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon delete"
                      onClick={() => handleDeleteItem(item.item_id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .dashboard-container {
          display: flex;
          min-height: 100vh;
          background: linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%);
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          color: #1e293b;
        }

        /* Sidebar Styles */
        .sidebar {
          width: 280px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          border-right: 1px solid #e2e8f0;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
          position: fixed;
          left: 0;
          top: 0;
          height: 100vh;
          display: flex;
          flex-direction: column;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          z-index: 1000;
          overflow-y: auto;
        }

        .sidebar.closed {
          transform: translateX(-280px);
        }

        .sidebar.opened {
          transform: translateX(0);
        }

        .sidebar-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 999;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        @media (max-width: 768px) {
          .sidebar-overlay.active {
            display: block;
            opacity: 1;
          }
        }

        .user-info-sidebar {
          padding: 30px 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          border-bottom: 1px solid #e2e8f0;
        }

        .user-avatar {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 20px;
          flex-shrink: 0;
        }

        .user-name {
          font-weight: 600;
          color: #1e293b;
        }

        .user-role {
          font-size: 12px;
          color: #64748b;
        }

        .sidebar-nav {
          flex: 1;
          padding: 30px 0;
        }

        .nav-label {
          padding: 0 20px;
          font-size: 12px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 15px;
        }

        .menu-item {
          padding: 15px 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 15px;
          color: #64748b;
          transition: all 0.2s ease;
          border-left: 3px solid transparent;
        }

        .menu-item:hover {
          background: #f1f5f9;
          color: #0d9488;
          border-left-color: #0d9488;
        }

        .menu-icon {
          font-size: 18px;
        }

        .menu-label {
          font-weight: 500;
        }

        .logout-section {
          padding: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .logout-btn {
          width: 100%;
          padding: 12px;
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .logout-btn:hover {
          background: #fecaca;
        }

        /* Main Content */
        .main-content {
          flex: 1;
          margin-left: 280px;
          display: flex;
          flex-direction: column;
          transition: margin-left 0.3s ease;
        }

        .main-content.full {
          margin-left: 0;
        }

        .header {
          background: white;
          padding: 20px 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .menu-toggle {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #0d9488;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .menu-toggle:hover {
          background: #f1f5f9;
        }

        .header-title {
          font-size: 24px;
          font-weight: 700;
          color: #0d9488;
        }

        .content-area {
          flex: 1;
          padding: 30px;
          overflow-y: auto;
        }

        .section-title {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 20px;
          margin-top: 30px;
        }

        .section-title:first-child {
          margin-top: 0;
        }

        /* Metrics Grid */
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .metric-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0;
        }

        .metric-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .metric-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 15px;
        }

        .metric-emoji {
          font-size: 24px;
        }

        .metric-label {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
        }

        .metric-value {
          font-size: 32px;
          font-weight: 700;
          color: #0d9488;
          margin-bottom: 15px;
        }

        .metric-footer {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .metric-bar {
          height: 4px;
          background: #e2e8f0;
          border-radius: 2px;
          overflow: hidden;
        }

        .metric-fill {
          height: 100%;
          background: linear-gradient(90deg, #0d9488, #14b8a6);
          border-radius: 2px;
        }

        .metric-details {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
        }

        .metric-total {
          color: #64748b;
          font-weight: 500;
        }

        .metric-trend {
          color: #0d9488;
          font-weight: 600;
        }

        /* Filters */
        .filters-section {
          background: white;
          padding: 20px;
          border-radius: 12px;
          margin-bottom: 20px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .filter-row {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 15px;
          align-items: center;
        }

        .search-box {
          width: 100%;
          padding: 10px 15px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
        }

        .search-box:focus {
          outline: none;
          border-color: #0d9488;
          box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
        }

        .category-filters {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 8px 16px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 20px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .filter-btn.active {
          background: #0d9488;
          color: white;
          border-color: #0d9488;
        }

        .filter-btn:hover {
          border-color: #0d9488;
        }

        .add-btn {
          padding: 10px 20px;
          background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .add-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
        }

        /* Alert Section */
        .alert-section {
          background: white;
          border-left: 4px solid #ef4444;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 30px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .alert-header {
          font-weight: 700;
          color: #dc2626;
          margin-bottom: 15px;
          font-size: 14px;
        }

        .alert-items {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .alert-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          background: #fef2f2;
          border-radius: 6px;
        }

        .alert-item-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .alert-item-name {
          font-weight: 600;
          color: #1e293b;
        }

        .alert-item-detail {
          font-size: 12px;
          color: #64748b;
        }

        .alert-badge {
          font-size: 18px;
        }

        /* Table Styles */
        .table-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .inventory-table {
          width: 100%;
          border-collapse: collapse;
        }

        .inventory-table thead {
          background: #f8fafc;
          border-bottom: 2px solid #e2e8f0;
        }

        .inventory-table th {
          padding: 15px 20px;
          text-align: left;
          font-weight: 600;
          font-size: 12px;
          text-transform: uppercase;
          color: #64748b;
        }

        .inventory-table td {
          padding: 15px 20px;
          border-bottom: 1px solid #e2e8f0;
          font-size: 14px;
        }

        .inventory-table tbody tr {
          transition: all 0.2s ease;
        }

        .inventory-table tbody tr:hover {
          background: #f8fafc;
        }

        .inventory-table tbody tr.row-warning {
          background: #fffbeb;
        }

        /* Mobile Card View */
        .inventory-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          border-left: 4px solid #0d9488;
          display: none;
        }

        .inventory-card.warning {
          background: #fffbeb;
          border-left-color: #f59e0b;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
          gap: 10px;
        }

        .card-title {
          font-weight: 700;
          font-size: 15px;
          color: #1e293b;
          flex: 1;
          word-break: break-word;
        }

        .card-status {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .card-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #e2e8f0;
          font-size: 13px;
        }

        .card-row:last-of-type {
          border-bottom: 1px solid #e2e8f0;
        }

        .card-label {
          color: #64748b;
          font-weight: 500;
        }

        .card-value {
          color: #1e293b;
          font-weight: 600;
          text-align: right;
        }

        .card-value.quantity {
          color: #0d9488;
          font-weight: 700;
        }

        .card-value.low-stock {
          color: #dc2626;
          font-weight: 700;
        }

        .card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e2e8f0;
          gap: 10px;
        }

        .card-category {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 16px;
          font-size: 11px;
          font-weight: 600;
        }

        .card-category[data-category="vegetables"] { background: #d1fae5; color: #065f46; }
        .card-category[data-category="fruits"] { background: #fce7f3; color: #831843; }
        .card-category[data-category="grains"] { background: #fed7aa; color: #92400e; }
        .card-category[data-category="dairy"] { background: #dbeafe; color: #0c2d6b; }
        .card-category[data-category="spices"] { background: #f3e8ff; color: #581c87; }
        .card-category[data-category="oil"] { background: #fef3c7; color: #78350f; }
        .card-category[data-category="other"] { background: #e5e7eb; color: #374151; }

        .card-actions {
          display: flex;
          gap: 8px;
        }

        .item-name {
          font-weight: 600;
          color: #1e293b;
        }

        .category-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .category-badge[data-category="vegetables"] { background: #d1fae5; color: #065f46; }
        .category-badge[data-category="fruits"] { background: #fce7f3; color: #831843; }
        .category-badge[data-category="grains"] { background: #fed7aa; color: #92400e; }
        .category-badge[data-category="dairy"] { background: #dbeafe; color: #0c2d6b; }
        .category-badge[data-category="spices"] { background: #f3e8ff; color: #581c87; }
        .category-badge[data-category="oil"] { background: #fef3c7; color: #78350f; }
        .category-badge[data-category="other"] { background: #e5e7eb; color: #374151; }

        .quantity {
          font-weight: 600;
          color: #0d9488;
        }

        .quantity .low-stock {
          color: #dc2626;
          font-weight: 700;
        }

        .status-cell {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
        }

        .status-badge.good { background: #d1fae5; color: #065f46; }
        .status-badge.warning { background: #fef3c7; color: #92400e; }
        .status-badge.expired { background: #fee2e2; color: #dc2626; }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .btn-icon {
          width: 36px;
          height: 36px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .btn-icon.edit:hover {
          background: #e0f2fe;
          border-color: #06b6d4;
        }

        .btn-icon.delete:hover {
          background: #fee2e2;
          border-color: #dc2626;
        }

        /* Modal Styles */
        .modal-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 2000;
          align-items: center;
          justify-content: center;
        }

        .modal-overlay.active {
          display: flex;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h2 {
          font-size: 18px;
          color: #1e293b;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #64748b;
          transition: color 0.2s ease;
        }

        .modal-close:hover {
          color: #1e293b;
        }

        .modal-form {
          padding: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .form-group input,
        .form-group select {
          padding: 10px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #0d9488;
          box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
        }

        .form-group.checkbox {
          flex-direction: row;
          align-items: center;
        }

        .form-group.checkbox label {
          margin-bottom: 0;
          margin-left: 8px;
          display: flex;
          align-items: center;
        }

        .form-group.checkbox input {
          margin: 0;
          width: auto;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .btn-cancel {
          padding: 10px 20px;
          background: #e2e8f0;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .btn-cancel:hover {
          background: #cbd5e1;
        }

        .btn-save {
          padding: 10px 20px;
          background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          color: white;
          transition: all 0.2s ease;
        }

        .btn-save:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .empty-state {
          background: white;
          border-radius: 12px;
          padding: 40px;
          text-align: center;
          color: #64748b;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .error-message {
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 12px;
          padding: 20px;
          color: #dc2626;
          text-align: center;
          margin: 20px;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .filter-row {
            grid-template-columns: 1fr;
          }

          .category-filters {
            order: 2;
          }
        }

        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-280px);
          }

          .sidebar.opened {
            transform: translateX(0);
            box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
          }

          .main-content {
            margin-left: 0 !important;
          }

          .header {
            padding: 12px 16px;
          }

          .header-title {
            font-size: 18px;
          }

          .content-area {
            padding: 20px 15px;
          }

          .metrics-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }

          .filter-row {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .category-filters {
            flex-wrap: nowrap;
            overflow-x: auto;
            padding-bottom: 5px;
            -webkit-overflow-scrolling: touch;
          }

          .filter-btn {
            flex-shrink: 0;
            padding: 8px 14px;
            font-size: 12px;
          }

          .add-btn {
            padding: 10px 16px;
            font-size: 14px;
            white-space: nowrap;
          }

          /* Hide table, show cards */
          .inventory-table {
            display: none;
          }

          .table-container {
            background: transparent;
            box-shadow: none;
            padding: 0;
          }

          .inventory-card {
            display: block;
          }

          .modal-content {
            width: 95%;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 15px;
          }

          .section-title {
            font-size: 16px;
            margin-bottom: 15px;
          }

          .alert-section {
            padding: 15px;
            margin-bottom: 20px;
          }

          .alert-item {
            padding: 8px;
            font-size: 12px;
          }

          .alert-item-name {
            font-size: 13px;
          }

          .alert-item-detail {
            font-size: 11px;
          }

          .filters-section {
            padding: 15px;
          }

          .metric-card {
            padding: 16px;
          }

          .metric-value {
            font-size: 28px;
          }

          .metric-emoji {
            font-size: 20px;
          }

          .metric-label {
            font-size: 11px;
          }

          .btn-icon {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }
        }

        @media (max-width: 480px) {
          .header {
            padding: 10px 12px;
          }

          .header-title {
            font-size: 16px;
          }

          .content-area {
            padding: 15px 10px;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .metric-card {
            padding: 12px;
          }

          .metric-value {
            font-size: 22px;
          }

          .metric-emoji {
            font-size: 18px;
          }

          .metric-label {
            font-size: 10px;
          }

          .section-title {
            font-size: 14px;
            margin-bottom: 12px;
          }

          .filter-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .search-box {
            padding: 8px 12px;
            font-size: 13px;
          }

          .category-filters {
            gap: 6px;
            padding-bottom: 0;
          }

          .filter-btn {
            padding: 6px 12px;
            font-size: 11px;
          }

          .add-btn {
            padding: 8px 12px;
            font-size: 12px;
          }

          .inventory-card {
            padding: 12px;
            margin-bottom: 10px;
          }

          .card-header {
            margin-bottom: 8px;
          }

          .card-title {
            font-size: 14px;
          }

          .card-row {
            padding: 6px 0;
            font-size: 12px;
          }

          .card-label {
            font-size: 12px;
          }

          .card-value {
            font-size: 12px;
          }

          .card-footer {
            margin-top: 8px;
            padding-top: 8px;
            gap: 6px;
          }

          .card-category {
            padding: 3px 8px;
            font-size: 10px;
          }

          .card-actions {
            gap: 6px;
          }

          .btn-icon {
            width: 36px;
            height: 36px;
            font-size: 16px;
          }

          .alert-section {
            padding: 12px;
            margin-bottom: 15px;
          }

          .alert-header {
            font-size: 13px;
          }

          .alert-item {
            padding: 6px;
          }

          .alert-item-name {
            font-size: 12px;
          }

          .alert-item-detail {
            font-size: 10px;
          }

          .alert-badge {
            font-size: 16px;
          }

          .filters-section {
            padding: 12px;
            margin-bottom: 15px;
          }

          .modal-content {
            width: 98%;
            border-radius: 8px;
          }

          .modal-header {
            padding: 15px;
          }

          .modal-header h2 {
            font-size: 16px;
          }

          .modal-close {
            font-size: 20px;
          }

          .modal-form {
            padding: 15px;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 12px;
            margin-bottom: 15px;
          }

          .form-group label {
            font-size: 13px;
            margin-bottom: 6px;
          }

          .form-group input,
          .form-group select {
            padding: 8px;
            font-size: 13px;
          }

          .form-actions {
            gap: 8px;
            margin-top: 15px;
            padding-top: 15px;
          }

          .btn-cancel,
          .btn-save {
            padding: 8px 16px;
            font-size: 12px;
          }

          .status-badge {
            padding: 3px 8px;
            font-size: 10px;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Sidebar Overlay for Mobile */}
        {isMobile && (
          <div 
            className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`sidebar ${!sidebarOpen ? 'closed' : ''} ${isMobile && sidebarOpen ? 'opened' : ''}`}>
          <div className="user-info-sidebar">
            <div className="user-avatar">
              {userInfo?.fullName?.charAt(0)?.toUpperCase() || 'O'}
            </div>
            <div>
              <div className="user-name">{userInfo?.fullName || 'Owner'}</div>
              <div className="user-role">Property Owner</div>
            </div>
          </div>

          <div className="sidebar-nav">
            <div className="nav-label">Main Menu</div>
            <MenuItem
              label="Dashboard"
              icon="📊"
              onClick={() => handleNavigation('hostel-owner-dashboard')}
            />
            <MenuItem
              label="Inventory Management"
              icon="📦"
              onClick={() => handleNavigation('hostel-owner-inventory')}
            />
            <MenuItem
              label="See Hostel Data"
              icon="🏢"
              onClick={() => handleNavigation('hostel-owner-hosteldata')}
            />
          </div>

          <div className="logout-section">
            <button className="logout-btn" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className={`main-content ${!sidebarOpen ? 'full' : ''}`}>
          <div className="header">
            <div className="header-left">
              <button
                className="menu-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              >
                {sidebarOpen ? '✕' : '☰'}
              </button>
              <div className="header-title">📦 Inventory Management</div>
            </div>
          </div>

          <div className="content-area">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Metrics */}
            <div className="section-title">📊 Inventory Statistics</div>
            <InventoryStats />

            {/* Filters */}
            <div className="section-title">🔍 Filter & Search</div>
            <div className="filters-section">
              <div className="filter-row">
                <input
                  type="text"
                  className="search-box"
                  placeholder="Search by item name or supplier..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                <div className="category-filters">
                  <button
                    className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                    onClick={() => handleCategoryChange('all')}
                  >
                    All
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      className={`filter-btn ${selectedCategory === cat ? 'active' : ''}`}
                      onClick={() => handleCategoryChange(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <button className="add-btn" onClick={() => setShowAddModal(true)}>
                  ➕ Add Item
                </button>
              </div>
            </div>

            {/* Low Stock Alert */}
            <LowStockAlert />

            {/* Inventory Table */}
            <div className="section-title">📋 Inventory Items</div>
            <InventoryTable />

            {/* Add/Edit Modal */}
            {(showAddModal || editingItem) && (
              <div 
                className="modal-overlay active"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingItem(null);
                }}
              >
                <AddEditModal
                  item={editingItem}
                  onClose={() => {
                    setShowAddModal(false);
                    setEditingItem(null);
                  }}
                  onSave={(formData) => {
                    if (editingItem) {
                      handleUpdateItem(editingItem.item_id, formData);
                    } else {
                      handleAddItem(formData);
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InventoryManagement;